import { ReactNode, useMemo, useState } from 'react';

import {
  ColumnDef,
  SortingState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
} from '@tanstack/react-table';

import { ScrollableTable } from './table/scrollable-table';
import { Permission } from '../../../../backend/src/auth/permissions/permission';
import { Role } from '@/api/queries/useGetRoles';
import { User } from '@/api/types/backend-dto-types';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

interface MatterUserRow {
  id: string;
  email: string;
  globalRoleName: string;
  roleOverrideName: string;
  isCurrentUser: boolean;
  isCreator: boolean;
  isAdmin: boolean;
  override?: { id: string; roleId: string; roleName: string };
}

interface MatterUsersTableProps {
  matterUsers: MatterUserRow[];
  overridesEnabled: boolean;
  canManageOverrides: boolean;
  canRemoveUsers: boolean;
  roles: Role[];
  isGlobalAdmin: boolean;
  additionalButtons?: ReactNode;
  onRoleOverrideChange: (userId: string, roleId: string) => void;
  onRemoveUser: (userId: string) => void;
}

const columnHelper = createColumnHelper<MatterUserRow>();

export function MatterUsersTable({
  matterUsers,
  overridesEnabled,
  canManageOverrides,
  canRemoveUsers,
  roles,
  isGlobalAdmin,
  additionalButtons,
  onRoleOverrideChange,
  onRemoveUser,
}: MatterUsersTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  // Deduplicate roles by name, keeping the first occurrence (matching users-table pattern)
  const uniqueRoles = useMemo(() => {
    const seen = new Set<string>();
    return roles.filter((r) => {
      if (seen.has(r.name)) return false;
      seen.add(r.name);
      return true;
    });
  }, [roles]);

  const hasSeatBasedUsers = useMemo(() => matterUsers.some((u) => u.globalRoleName === 'Seat-based'), [matterUsers]);

  const columns = useMemo(() => {
    const cols: ColumnDef<MatterUserRow, any>[] = [
      columnHelper.accessor('email', {
        header: 'Email',
        cell: ({ row }) => (
          <div className="flex items-center gap-2 lowercase">
            {row.original.email}
            {row.original.isCurrentUser && <span className="text-xs text-gray-500">(You)</span>}
            {row.original.isCreator && <span className="text-xs text-gray-500">(Creator)</span>}
          </div>
        ),
      }),
      columnHelper.accessor('globalRoleName', {
        header: 'Global Role',
        cell: ({ row }) => (
          <span className={`text-sm ${row.original.override ? 'text-gray-400' : 'text-gray-700'}`}>
            {row.original.globalRoleName}
          </span>
        ),
      }),
    ];

    if (overridesEnabled) {
      cols.push(
        columnHelper.accessor('roleOverrideName', {
          header: 'Role Override',
          cell: ({ row }) => {
            const { isCreator, isAdmin, isCurrentUser, override } = row.original;
            // Admins (global UserManage) can't be overridden. UserManage is
            // org-scoped and isn't replaced by overrides. Creators can't
            // demote themselves.
            const canOverride = !isAdmin && !(isCreator && isCurrentUser);

            if (canOverride && canManageOverrides) {
              return (
                <Select
                  value={override?.roleId ?? 'none'}
                  onValueChange={(roleId: string) => onRoleOverrideChange(row.original.id, roleId)}
                >
                  <SelectTrigger className="-m-0.5 h-auto w-40 border-none p-1 pl-2 focus:outline-none">
                    <span className={!override ? 'text-gray-400' : ''}>
                      <SelectValue placeholder="No override" />
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No override</SelectItem>
                    {uniqueRoles
                      .filter((r) => r.name !== 'Administrator' || isGlobalAdmin || isCreator)
                      .filter((r) => r.name !== 'Seat-based' || hasSeatBasedUsers)
                      .map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              );
            }

            if (isAdmin || (isCreator && isCurrentUser)) {
              const reason = isAdmin
                ? 'Administrators have full permissions and cannot be overridden'
                : 'You cannot change your own role on a Matter you created. Ask an admin if you need to be demoted.';
              return (
                <div className="flex items-center gap-1.5 px-0.5 py-0.5 pl-1.5 text-gray-500">
                  {override?.roleName ?? (isAdmin ? 'Administrator' : 'Administrator')}
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <button className="shrink-0">
                        <Icon className="lucide--info h-3 w-3 text-gray-400" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p className="text-xs">{reason}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              );
            }

            return <div className="px-0.5 py-0.5 pl-1.5 text-gray-500">{override?.roleName ?? '-'}</div>;
          },
        }),
      );
    }

    if (canRemoveUsers) {
      cols.push(
        columnHelper.display({
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => (
            <div>
              {!row.original.isCreator && (
                <PermissionGate permission={Permission.MatterRemoveUsers} uiMode="disabled">
                  <Button size="sm" onClick={() => onRemoveUser(row.original.id)}>
                    <Icon className="lucide--x" />
                    Remove
                  </Button>
                </PermissionGate>
              )}
            </div>
          ),
        }),
      );
    }

    return cols;
  }, [overridesEnabled, canManageOverrides, canRemoveUsers, roles, onRoleOverrideChange, onRemoveUser]);

  const table = useReactTable({
    data: matterUsers,
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    globalFilterFn: 'includesString',
    initialState: { pagination: { pageSize: 10 } },
    state: { sorting, globalFilter },
  });

  return (
    <ScrollableTable
      table={table}
      globalFilter={globalFilter}
      setGlobalFilter={setGlobalFilter}
      plural="users"
      additionalButtons={additionalButtons}
      className="-mx-8"
    />
  );
}

/** Helper to build MatterUserRow[] from the data sources */
export function buildMatterUserRows(
  matterUsers: User[],
  orgUserMap: Map<string, User>,
  overrideMap: Map<string, { id: string; roleId: string; roleName: string }>,
  currentUserId: string | undefined,
  matterCreatorId: string | undefined,
): MatterUserRow[] {
  return matterUsers.map((u) => {
    const orgUser = orgUserMap.get(u.id);
    const override = overrideMap.get(u.id);
    const isCreator = u.id === matterCreatorId;
    const isAdmin = orgUser?.role?.permissions?.includes(Permission.UserManage) ?? false;
    return {
      id: u.id,
      email: u.email,
      globalRoleName: orgUser?.role?.name ?? 'Unknown',
      roleOverrideName: isAdmin ? 'Administrator' : isCreator ? 'Creator' : (override?.roleName ?? ''),
      isCurrentUser: u.id === currentUserId,
      isCreator,
      isAdmin,
      override,
    };
  });
}
